const LAST_SELECTED_ITEM_KEY = 'last_selected_item';
const STORAGE_VIEWED = 'last_viewed_item';
const CART_LIST_HISTORY_KEY = 'cart_list_history';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

interface StoredItemMemory {
  item_id: string;
  item_list_name: string;
  timestamp: number;
}

interface CartHistoryEntry {
  item_id: string;
  item_list_name: string;
}

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseJSON<T>(value: string | null): T | null {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function isFreshTimestamp(timestamp: number): boolean {
  const elapsed = Date.now() - timestamp;
  return elapsed >= 0 && elapsed < DAY_IN_MS;
}

function isStoredItemMemory(value: unknown): value is StoredItemMemory {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.item_id === 'string' &&
    typeof candidate.item_list_name === 'string' &&
    typeof candidate.timestamp === 'number'
  );
}

function normalizeStoredItemMemory(value: unknown): StoredItemMemory | null {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  const itemId = candidate.item_id;
  const itemListName = candidate.item_list_name;
  const timestamp = candidate.timestamp;

  if (
    typeof itemId !== 'string' ||
    typeof itemListName !== 'string' ||
    typeof timestamp !== 'number'
  ) {
    return null;
  }

  return {
    item_id: itemId,
    item_list_name: itemListName,
    timestamp,
  };
}

function resolveIdFromUnknownItem(item: Record<string, any> | null | undefined): string | null {
  const id =
    item?.productID ??
    item?.productId ??
    item?.item_group_id ??
    item?.item_id ??
    item?.id ??
    null;

  return id == null ? null : String(id);
}

export function resolveItemId(item: Record<string, any> | null | undefined): string | null {
  return resolveIdFromUnknownItem(item);
}

function readStoredItem(key: string): StoredItemMemory | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const parsed = parseJSON<unknown>(storage.getItem(key));
    const normalized = normalizeStoredItemMemory(parsed);
    return normalized && isStoredItemMemory(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function writeStoredItem(key: string, value: StoredItemMemory): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Analytics must never break storefront UI
  }
}

function readCartHistory(): CartHistoryEntry[] {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const parsed = parseJSON<unknown>(storage.getItem(CART_LIST_HISTORY_KEY));
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry): entry is CartHistoryEntry => (
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as Record<string, unknown>).item_id === 'string' &&
      typeof (entry as Record<string, unknown>).item_list_name === 'string'
    ));
  } catch {
    return [];
  }
}

function writeCartHistory(history: CartHistoryEntry[]): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(CART_LIST_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // Analytics must never break storefront UI
  }
}

function getLatestItemListNameById(history: CartHistoryEntry[]) {
  const byId = new Map<string, string>();

  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = history[index];
    if (!byId.has(entry.item_id)) {
      byId.set(entry.item_id, entry.item_list_name);
    }
  }

  return byId;
}

export function saveSelectedItem(productId: string | number | null | undefined, listName: string | null | undefined) {
  if (productId == null || !listName) return;

  const payload: StoredItemMemory = {
    item_id: String(productId),
    item_list_name: String(listName),
    timestamp: Date.now(),
  };

  writeStoredItem(LAST_SELECTED_ITEM_KEY, payload);
}

export function saveViewedItem(productId: string | number | null | undefined, listName: string | null | undefined) {
  if (productId == null) return;

  const payload: StoredItemMemory = {
    item_id: String(productId),
    item_list_name: String(listName ?? 'PDP'),
    timestamp: Date.now(),
  };

  writeStoredItem(STORAGE_VIEWED, payload);
}

export function resolveItemListName(params: {
  event: string;
  item: Record<string, any> | null | undefined;
  itemListName: string | null | undefined;
}) {
  const { event, item, itemListName } = params;
  const currentListName = itemListName ?? null;
  const isResolvableEvent = event === 'VIEW_ITEM' || event === 'ADD_TO_CART';

  if (!isResolvableEvent) return currentListName;
  const currentItemId = resolveIdFromUnknownItem(item);

  if (event === 'VIEW_ITEM') {
    if (!currentItemId) return 'PDP';

    const selectedItem = readStoredItem(LAST_SELECTED_ITEM_KEY);
    if (!selectedItem) return 'PDP';

    if (selectedItem.item_id === currentItemId && isFreshTimestamp(selectedItem.timestamp)) {
      return selectedItem.item_list_name;
    }

    return 'PDP';
  }

  // For add_to_cart, keep explicit non-PDP origin untouched.
  if (currentListName !== 'PDP' && currentListName !== null) return currentListName;
  if (!currentItemId) return currentListName;

  const viewedItem = readStoredItem(STORAGE_VIEWED);
  if (viewedItem && viewedItem.item_id === currentItemId && isFreshTimestamp(viewedItem.timestamp)) {
    return viewedItem.item_list_name;
  }

  return currentListName === 'PDP' ? 'PDP' : null;
}

export function appendCartHistory(productId: string | number | null | undefined, listName: string | null | undefined) {
  if (productId == null) return;

  const history = readCartHistory();
  history.push({
    item_id: String(productId),
    item_list_name: listName ? String(listName) : 'PDP',
  });

  writeCartHistory(history);
}

export function getCartHistoryItemListNameMap() {
  const history = readCartHistory();
  if (!history.length) return null;

  return getLatestItemListNameById(history);
}

export function resolveItemListNameFromCartHistory(
  item: Record<string, any> | null | undefined,
  cartHistoryMap: Map<string, string> | null = null
) {
  const itemId = resolveIdFromUnknownItem(item);
  const map = cartHistoryMap ?? getCartHistoryItemListNameMap();

  if (!itemId) return undefined;

  const resolved = map?.get(itemId);
  return resolved;
}

function pickSelectedItemFromResolvedItems(items: Record<string, any>[], source: Record<string, any>): Record<string, any> | null {
  if (!Array.isArray(items) || items.length === 0) return null;

  if (typeof source?.index === 'number') {
    const indexed = items.find((item) => item?.index === source.index) ?? items[source.index];
    if (indexed && typeof indexed === 'object') return indexed;
  }

  return items[0];
}

export function persistSelectItemOriginFromResolvedItems(items: Record<string, any>[], source: Record<string, any>) {
  const selectedItem = pickSelectedItemFromResolvedItems(items, source);
  if (!selectedItem) return;

  const productId = resolveIdFromUnknownItem(selectedItem);
  const listName = selectedItem?.item_list_name ?? source?.itemListName ?? source?.item_list_name ?? null;
  saveSelectedItem(productId, listName);
}
